import styled from "@emotion/styled";
import {
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
} from "@mui/material";
import { MenuItem } from "custom-hooks/use-user-settings-menu-items.hook";
import Link from "next/link";
import React from "react";
import { CPagesPath } from "shared/models/const/pages-path";
import { INavLink } from "shared/models/interfaces/components/navlinks.interface";

interface IMobileMenuItemProps {
  item: INavLink | MenuItem;
  handleOpen?: () => void;
}
export default function MobileMenuItem({
  item,
  handleOpen,
}: IMobileMenuItemProps) {
  const itemButtonStyles = getMobileItemButtonStyles(item.link);

  return (
    <>
      <Divider />
      <ListItem>
        {handleOpen ? (
          <ListItemButton sx={itemButtonStyles} onClick={handleOpen}>
            <ItemImageIcon item={item} />
            <ListItemTextCustom>{item.slug}</ListItemTextCustom>
          </ListItemButton>
        ) : (item as INavLink).target ? (
          <ListItemButton
            component="a"
            href={item.link ?? ""}
            target={(item as INavLink).target}
            rel="noopener noreferrer"
            sx={itemButtonStyles}
          >
            <ItemImageIcon item={item} />
            <ListItemTextCustom>{item.slug}</ListItemTextCustom>
          </ListItemButton>
        ) : (
          <Link href={item.link ?? ""}>
            <ListItemButton LinkComponent={"a"} sx={itemButtonStyles}>
              <ItemImageIcon item={item} />
              <ListItemTextCustom>{item.slug}</ListItemTextCustom>
            </ListItemButton>
          </Link>
        )}
      </ListItem>
      <Divider />
      {(item as MenuItem).children?.length &&
        (item as MenuItem).children?.map((item, i) => (
          <MobileMenuItem key={i} item={item} />
        ))}
    </>
  );
}

const getMobileItemButtonStyles = (link?: string) => {
  const baseStyles = {
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  if (link !== CPagesPath.es.join && link !== CPagesPath.es.franchises) {
    return baseStyles;
  }

  return {
    ...baseStyles,
    mx: -0.5,
    my: 0.5,
    minHeight: 46,
    px: 1,
    borderRadius: "999px",
    color: "var(--color-text-white)",
    backgroundColor:
      link === CPagesPath.es.join
        ? "var(--color-brand-blue-main)"
        : "var(--color-brand-red-main)",
    boxShadow: "0 12px 26px rgba(0, 0, 0, 0.16)",
    "&:hover": {
      backgroundColor:
        link === CPagesPath.es.join
          ? "var(--color-brand-blue-secondary)"
          : "var(--color-brand-red-main)",
    },
    "& .MuiListItemIcon-root": {
      color: "var(--color-text-white)",
      minWidth: 30,
    },
    "& .MuiSvgIcon-root": {
      color: "var(--color-text-white)",
    },
    "& .MuiTypography-root": {
      color: "var(--color-text-white)",
      fontSize: "0.86rem",
      fontWeight: 700,
      lineHeight: 1.15,
      overflow: "visible",
      textOverflow: "clip",
      width: "auto",
    },
  };
};

interface IItemImageIconProps {
  item: INavLink;
}

const ItemImageIcon = ({ item }: IItemImageIconProps) => (
  <ListItemIcon sx={{ minWidth: 36 }}>
    {item.iconComponent ? (
      item.iconComponent
    ) : typeof item.icon === "string" ? (
      <Image src={item.icon} alt="profile icon" />
    ) : (
      item.icon && <SvgIcon component={item.icon} inheritViewBox />
    )}
  </ListItemIcon>
);

const Image = styled.img`
  width: 30px;
  height: 20px;
  filter: grayscale(1);
`;

const ListItemTextCustom = styled(ListItemText)`
  .MuiTypography-root {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 155px;
  }
`;
